import React, { useState } from 'react';
import { Modal, Select, Checkbox, message, Spin } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import styles from '../../styles/reportDetail.module.less';
import xiazaiImage from '../../../images/xiazai.png';
import fenxiangImage from '../../../images/fenxiang.png';
import historyImage from '../../../images/history.png';
import { useSearchParams } from 'react-router-dom';
import { utils } from '@ohif/core';
import { useAppConfig } from '@state';
const { apiService } = utils;

// 组件属性接口，补充类型定义避免隐式any
interface Tab1ContentProps {
  data: {
    bodypart_name?: string;
    modality_code?: string;
    chinese_name?: string;
    sex?: string;
    age?: string;
    study_time?: string | number | Date;
    check_view2?: string;
    diagnosis_result2?: string;
    sh?: string;
    approver_time?: string | number | Date;
    register_key?: string;
  };
  hidename: boolean;
  isShareMode: boolean;
  currentHost: string;
  fromFlag: string;
  onShare?: () => void;
  onDownload?: () => void;
  onHistory?: () => void;
}

const Tab1Content: React.FC<Tab1ContentProps> = ({
  data,
  hidename,
  fromFlag,
  isShareMode,
  currentHost,
  studyInstanceUid,
  onShare,
  onDownload,
  onHistory,
}) => {
  // 状态管理
  const [selectedTime, setSelectedTime] = useState('1day');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isHideName, setIsHideName] = useState(false);
  const [isEncryptedShare, setIsEncryptedShare] = useState(false);
  const [shareCode, setShareCode] = useState(''); // 分享码
  const [expirationDate, setExpirationDate] = useState(''); // 有效期
  const [loading, setLoading] = useState(false); // 加载状态
  const [searchParams] = useSearchParams();
  const [appConfig] = useAppConfig();
  const [messageApi, contextHolder] = message.useMessage();

  // 有效期配置：统一管理有效时间选项
  const validityOptions = {
    '1day': { text: '1天内有效', desc: '分享将在1天后失效，无法继续查看', minutes: 1 * 24 * 60 },
    '7day': { text: '7天内有效', desc: '分享将在7天后失效，无法继续查看', minutes: 7 * 24 * 60 },
    '15day': {
      text: '15天内有效',
      desc: '分享将在15天后失效，无法继续查看',
      minutes: 15 * 24 * 60,
    },
  };

  // 日期格式化工具：统一处理时间显示
  const calculateExpirationDate = (date: string | number | Date) => {
    const currentDate = new Date(date);
    // 处理无效日期
    if (isNaN(currentDate.getTime())) return '暂无数据';
    return currentDate
      .toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
      .replace(/\//g, '-');
  };

  // 分享设置弹窗 - 确认按钮逻辑
  const handleShareModalOk = () => {
    if (!data?.register_key) {
      messageApi.error('数据缺失，无法生成分享');
      return;
    }

    const formData = new FormData();
    formData.append(
      'targetUrl',
      `/reportdetail?flag=share&hidename=${isHideName}&register_key=${data.register_key}`
    );
    formData.append('minutesValid', validityOptions[selectedTime].minutes.toString()); // 确保参数类型正确
    setLoading(true);

    apiService({
      uri: '/api/Share/Generate',
      method: 'POST',
      data: formData,
      type: null,
    })
      .then(res => {
        setLoading(false);
        setShareCode(res?.Code || ''); // 空值兜底
        setExpirationDate(calculateExpirationDate(res?.ExpireAt || new Date())); // 兜底默认时间
        setIsShareModalOpen(false);
        setIsLinkModalOpen(true);
        onShare?.();
      })
      .catch(err => {
        setLoading(false);
        messageApi.error('生成分享信息失败，请重试', err); // 优化错误提示
      });
  };

  // 核心：生成分享链接（含地址栏原有参数）
  const getShareLink = (): string => {
    if (!data?.register_key || !shareCode) return '';
    // 1. 复制地址栏原有参数（避免修改原对象）
    const params = new URLSearchParams(searchParams.toString());
    // 2. 添加/更新分享所需参数（同名参数会覆盖原有，符合业务逻辑）
    params.set('flag', 'share');
    params.set('code', shareCode);
    params.set('isEncryptedShare', isEncryptedShare.toString());
    params.set('hidename', isHideName.toString());
    params.set('expirationDate', expirationDate);
    params.set('register_key', data.register_key);
    // 3. 拼接最终链接（根据加密状态切换路径）
    const basePath = isEncryptedShare ? '/accesscode' : '/shareReportDetail';
    return `${currentHost}${basePath}?${params.toString()}`;
  };

  // 分享链接弹窗 - 复制按钮逻辑
  const handleLinkModalOk = () => {
    const shareLink = getShareLink();
    if (!shareLink) {
      messageApi.error('链接生成失败，请重试');
      return;
    }

    // 拼接复制内容
    const fullContent = `链接：${shareLink}${isEncryptedShare ? `\n查看码：${shareCode}` : ''}\n有效期：${expirationDate}`;

    // 复制到剪贴板（兼容新旧API）
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(fullContent)
        .then(() => {
          messageApi.success('链接已复制到剪贴板');
          setIsLinkModalOpen(false);
        })
        .catch(() => messageApi.error('复制失败，请手动复制'));
    } else {
      // 降级方案：创建临时文本框
      try {
        const textarea = document.createElement('textarea');
        textarea.value = fullContent;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        messageApi.success('链接已复制到剪贴板');
        setIsLinkModalOpen(false);
      } catch (err) {
        messageApi.error('复制失败，请手动复制');
      }
    }
  };

  // 打开下载弹窗逻辑
  const handleDownloadModalOpen = () => {
    const formData = new FormData();
    formData.append('targetUrl', '');
    formData.append('minutesValid', (7 * 24 * 60).toString()); // 固定7天有效期
    setLoading(true);

    apiService({
      uri: '/api/Share/Generate',
      method: 'POST',
      data: formData,
      type: null,
    })
      .then(res => {
        setLoading(false);
        setShareCode(res?.Code || '');
        setExpirationDate(calculateExpirationDate(res?.ExpireAt || new Date()));
        setIsDownloadModalOpen(true);
      })
      .catch(err => {
        setLoading(false);
        messageApi.error('生成下载信息失败，请重试', err);
      });
  };

  // 核心：生成下载链接（含地址栏原有参数）
  const getDownloadLink = (): string => {
    if (!data?.register_key || !shareCode) return '';
    // 1. 复制地址栏原有参数
    const params = new URLSearchParams(searchParams.toString());
    // 2. 添加/更新下载所需参数
    params.set('flag', 'download');
    params.set('register_key', data.register_key);
    params.set('code', shareCode);
    params.set('expirationDate', expirationDate);
    if (studyInstanceUid) params.set('studyUID', studyInstanceUid); // 可选参数判断
    // 3. 拼接最终下载链接
    return `${currentHost}/accesscode?${params.toString()}`;
  };

  // 下载弹窗 - 复制按钮逻辑
  const handleDownloadModalOk = () => {
    const downloadLink = getDownloadLink();
    if (!downloadLink) {
      messageApi.error('下载链接生成失败，请重试');
      return;
    }

    // 拼接复制内容
    const fullContent = `链接：${downloadLink}\n查看码：${shareCode}\n有效期：${expirationDate}`;

    // 复制逻辑（与分享链接一致，保证体验统一）
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(fullContent)
        .then(() => {
          messageApi.success('下载链接已复制到剪贴板');
          setIsDownloadModalOpen(false);
        })
        .catch(() => messageApi.error('复制失败，请手动复制'));
    } else {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = fullContent;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        messageApi.success('下载链接已复制到剪贴板');
        setIsDownloadModalOpen(false);
      } catch (err) {
        messageApi.error('复制失败，请手动复制');
      }
    }
  };

  // 控制是否显示历史检查模块（返回列表：/userreport 或 /userreport-portal）
  const fromSource = searchParams.get('from');
  const showHistory =
    fromSource === 'userreport' || fromSource === 'userreportportal';

  return (
    <>
      {contextHolder}
      <div className={styles.contentBoxMainWrap}>
        {/* 报告主体内容区 */}
        <div className={styles.contentBoxMain}>
          <h3 className={styles.reportTitle}>
            {data?.bodypart_name || '未知部位'}
            {data?.modality_code && `(${data.modality_code})`}
          </h3>
          <p className={styles.hospital}>{appConfig.hospitalName}</p>
          <hr className={styles.divider} />

          {/* 患者基础信息 */}
          <div className={styles.infoSection}>
            <div className={styles.infoItem}>
              <span className={styles.label}>患者信息：</span>
              <span>
                {hidename ? 'XXX' : data?.chinese_name || '未知'} /{data?.sex || '未知'} /
                {data?.age || '未知'}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.label}>检查时间：</span>
              <span>
                {data?.study_time ? calculateExpirationDate(data.study_time) : '暂无数据'}
              </span>
            </div>
          </div>

          {/* 检查所见 */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>检查所见</h3>
            <p>{data?.check_view2 || '暂无数据'}</p>
          </div>

          {/* 检查印象 */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>检查印象</h3>
            <p>{data?.diagnosis_result2 || '暂无数据'}</p>
          </div>

          <hr className={styles.divider} />

          {/* 审核信息 */}
          <div className={styles.infoSection}>
            <div className={styles.infoItem}>
              <span className={styles.label}>审核医生：</span>
              <span>{data?.sh || '暂无数据'}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.label}>审核时间：</span>
              <span>
                {data?.approver_time ? calculateExpirationDate(data.approver_time) : '暂无数据'}
              </span>
            </div>
          </div>
        </div>

        {/* 操作按钮区（非分享模式显示） */}
        {!isShareMode && (
          <>
            {appConfig.isShowShareDownloadButton && (
              <div className={styles.contentBox}>
              <div className={styles.actionButtons}>
                {/* 分享按钮 */}
                <div
                  className={styles.actionItem}
                  onClick={() => setIsShareModalOpen(true)}
                  aria-label="分享影像"
                >
                  <img
                    src={fenxiangImage}
                    alt="分享"
                    style={{ width: 30, height: 30 }}
                  />
                  <p>分享影像</p>
                </div>
                {/* 下载按钮 */}
                <div
                  className={styles.actionItem}
                  onClick={handleDownloadModalOpen}
                  aria-label="下载影像"
                >
                  <img
                    src={xiazaiImage}
                    alt="下载"
                    style={{ width: 30, height: 30 }}
                  />
                  <p>下载影像</p>
                </div>
              </div>
            </div>
            )}

            {/* 历史检查模块（特定条件显示） */}
            {showHistory && (
              <div className={styles.contentBox}>
                <div
                  className={styles.historySection}
                  onClick={onHistory}
                  aria-label="查看历史检查"
                >
                  <img
                    src={historyImage}
                    alt="历史检查"
                    style={{ width: 30, height: 30 }}
                  />
                  <div className={styles.historyText}>
                    <h3>历史检查</h3>
                    <p>便捷查看历史影像</p>
                  </div>
                  <RightOutlined />
                </div>
              </div>
            )}
          </>
        )}

        {/* 免责声明区 */}
        <div className={styles.contentBoxTips}>
          <div className={styles.disclaimer}>
            <span className={styles.disclaimerTitle}>免责声明：</span>
            仅供患者和临床医生参考，具体诊断结果请以专业医疗机构出具的实际报告为准，并在医生的指导下进行治疗。
          </div>
        </div>

        {/* 1. 分享设置弹窗 */}
        <Modal
          title="分享设置"
          open={isShareModalOpen}
          onOk={handleShareModalOk}
          onCancel={() => setIsShareModalOpen(false)}
          okText="确定"
          cancelButtonProps={{ style: { display: 'none' } }}
          okButtonProps={{ style: { width: '100%', height: 40, fontSize: 16 } }}
          destroyOnHidden // 关闭时销毁，避免状态残留
        >
          <Spin spinning={loading}>
            <div className={styles.shareModalContent}>
              <div className={styles.shareModalItem}>
                <span className={styles.label}>有效时间：</span>
                <Select
                  value={selectedTime}
                  style={{ width: 150 }}
                  onChange={setSelectedTime}
                  options={Object.entries(validityOptions).map(([value, { text }]) => ({
                    value,
                    label: text,
                  }))}
                  showSearch={false}
                />
              </div>
              <div className={styles.shareModalItem}>
                <h3 className={styles.modalSubtitle}>{validityOptions[selectedTime].text}</h3>
                <p className={styles.modalDesc}>{validityOptions[selectedTime].desc}</p>
              </div>
              <div className={`${styles.shareModalItem} ${styles.shareBox}`}>
                <Checkbox
                  checked={isHideName}
                  onChange={e => setIsHideName(e.target.checked)}
                >
                  匿名分享
                </Checkbox>
                <Checkbox
                  checked={isEncryptedShare}
                  onChange={e => setIsEncryptedShare(e.target.checked)}
                >
                  加密分享
                </Checkbox>
              </div>
            </div>
          </Spin>
        </Modal>

        {/* 2. 分享链接弹窗 */}
        <Modal
          title="分享链接"
          open={isLinkModalOpen}
          onOk={handleLinkModalOk}
          onCancel={() => setIsLinkModalOpen(false)}
          okText="复制链接"
          cancelButtonProps={{ style: { display: 'none' } }}
          okButtonProps={{ style: { width: '100%', height: 40, fontSize: 16 } }}
          destroyOnHidden
        >
          <Spin spinning={loading}>
            <div className={styles.linkModalContent}>
              <div className={styles.linkModalItem}>
                <div>
                  <span className={styles.label}>链接：</span>
                  <span className={styles.value}>{getShareLink()}</span>{' '}
                  {/* 显示含原有参数的链接 */}
                </div>
                {isEncryptedShare && (
                  <div>
                    <span className={styles.label}>查看码：</span>
                    <span className={styles.value}>{shareCode}</span>
                  </div>
                )}
                <div>
                  <span className={styles.label}>有效期：</span>
                  <span className={styles.value}>{expirationDate}</span>
                </div>
              </div>
            </div>
          </Spin>
        </Modal>

        {/* 3. 下载弹窗 */}
        <Modal
          title="影像下载须知"
          open={isDownloadModalOpen}
          onOk={handleDownloadModalOk}
          onCancel={() => setIsDownloadModalOpen(false)}
          okText="复制链接"
          cancelButtonProps={{ style: { display: 'none' } }}
          okButtonProps={{ style: { width: '100%', height: 40, fontSize: 16 } }}
          destroyOnHidden
        >
          <div className={styles.downloadModalContent}>
            <div className={styles.downloadInstructions}>
              <p>
                1、此次下载的图像为专业影像数据压缩包，需使用<span>专业浏览工具</span>打开。
              </p>
              <p>2、建议使用电脑在WIFI环境下复制下方链接进行下载操作。</p>
              <p>3、影像内容为个人隐私，下载后请妥善保管。</p>
            </div>
            <Spin spinning={loading}>
              <div className={styles.linkModalItem}>
                <div>
                  <span className={styles.label}>链接：</span>
                  <span className={styles.value}>{getDownloadLink()}</span>{' '}
                  {/* 显示含原有参数的链接 */}
                </div>
                <div>
                  <span className={styles.label}>查看码：</span>
                  <span className={styles.value}>{shareCode}</span>
                </div>
                <div>
                  <span className={styles.label}>有效期：</span>
                  <span className={styles.value}>{expirationDate}</span>
                </div>
              </div>
            </Spin>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default Tab1Content;
